import { z } from "zod";
import type { StateCreator } from "zustand/vanilla";

/**
 * exportTargetSchema is the itinerary the export modal is open for, plus an
 * optional segment to narrow the export to a single segment (null = the whole
 * itinerary). Zod-first because the value is shared with the export-open tool
 * input and validated when an agent opens the modal; a null target (modal
 * closed) lives outside the schema as the absence of a value.
 */
export const exportTargetSchema = z.object({
  itineraryId: z.string(),
  segmentId: z.string().nullable(),
});

/**
 * ExportTarget is the itinerary (and optional segment) the export modal is open
 * for, derived from exportTargetSchema. The export slot holds this or null (the
 * modal is closed).
 */
export type ExportTarget = z.infer<typeof exportTargetSchema>;

/**
 * collectionsTargetSchema is the entity the manage-collections modal is open for:
 * its kind (itinerary or zone) and id. The modal is shared across both entity
 * families (one membership picker, one create-inline form), so the target carries
 * which family it serves and the membership tools branch on it. Zod-first because
 * the value is the manage-open tool input and is validated when an agent opens the
 * modal; a null target (modal closed) lives outside the schema as the absence of a
 * value.
 */
export const collectionsTargetSchema = z.object({
  entity: z.enum(["itinerary", "zone"]),
  entityId: z.string(),
});

/**
 * CollectionsTarget is the entity the manage-collections modal is open for,
 * derived from collectionsTargetSchema. The collections slot holds this or null
 * (the modal is closed).
 */
export type CollectionsTarget = z.infer<typeof collectionsTargetSchema>;

/**
 * editingCollectionSchema is the collection the edit-collection modal is open
 * for: its id plus the metadata the modal seeds its fields from (name +
 * description + privacy — the full set the backend update takes). The
 * edit-collection slot holds this or null (the modal is closed); a non-null
 * value both signals the modal is open and carries its seed, mirroring how the
 * collections target drives the manage modal. Zod-first because the value is the
 * edit-open tool input, validated at the bus boundary.
 */
export const editingCollectionSchema = z.object({
  description: z.string(),
  id: z.string(),
  isPrivate: z.boolean(),
  name: z.string(),
});

/**
 * EditingCollection is the collection the edit-collection modal is bound to,
 * derived from editingCollectionSchema. The edit-collection slot holds this or
 * null (the modal is closed).
 */
export type EditingCollection = z.infer<typeof editingCollectionSchema>;

/**
 * sharingCollectionSchema is the collection the share-collection modal is open
 * for: its id and its name (the dialog's subject line). The share-collection slot
 * holds this or null (the modal is closed); a non-null value both signals the
 * modal is open and carries its seed, mirroring how the edit-collection slot
 * drives the edit modal. Zod-first because the value is the share-open tool input,
 * validated at the bus boundary.
 */
export const sharingCollectionSchema = z.object({
  id: z.string(),
  name: z.string(),
});

/**
 * SharingCollection is the collection the share-collection modal is bound to,
 * derived from sharingCollectionSchema. The share-collection slot holds this or
 * null (the modal is closed).
 */
export type SharingCollection = z.infer<typeof sharingCollectionSchema>;

/**
 * CollectionsSliceState is the set of collection-surface slots: the viewed
 * collection, the manage-collections target, the edit-collection binding, the
 * share-collection binding, and the export target.
 */
export interface CollectionsSliceState {
  /**
   * activeCollectionId is the collection a search-result activation points at,
   * so the collections surface opens directly on that one. Null when no
   * collection is being viewed (the listing screen, or a different surface is
   * active).
   */
  activeCollectionId: string | null;
  collectionsTarget: CollectionsTarget | null;
  /**
   * collectionToEdit is the collection the edit-collection modal is bound to, or
   * null when that modal is closed. A non-null value mounts the modal seeded with
   * the collection's metadata; the modal's submit updates the collection through
   * the bus and clears the slot.
   */
  collectionToEdit: EditingCollection | null;
  /**
   * collectionToShare is the collection the share-collection modal is bound to,
   * or null when that modal is closed. A non-null value mounts the modal seeded
   * with the collection's id and name; closing it clears the slot through the bus.
   */
  collectionToShare: SharingCollection | null;
  exportTarget: ExportTarget | null;
}

/**
 * CollectionsSliceActions are the whole-slot setters that drive the collection
 * surfaces, reached only through the tool bus.
 */
export interface CollectionsSliceActions {
  setActiveCollectionId: (collectionId: string | null) => void;
  setCollectionsTarget: (target: CollectionsTarget | null) => void;
  /**
   * setCollectionToEdit opens the edit-collection modal bound to a collection
   * (with its seed metadata), or closes it with null. The edit-open/close tools
   * drive it, mirroring setCollectionsTarget.
   */
  setCollectionToEdit: (collection: EditingCollection | null) => void;
  /**
   * setCollectionToShare opens the share-collection modal bound to a collection
   * (with its id and name), or closes it with null. The share-open/close tools
   * drive it, mirroring setCollectionToEdit.
   */
  setCollectionToShare: (collection: SharingCollection | null) => void;
  setExportTarget: (target: ExportTarget | null) => void;
}

/**
 * collectionsInitialState seeds every collection-surface slot closed.
 */
export const collectionsInitialState: CollectionsSliceState = {
  activeCollectionId: null,
  collectionsTarget: null,
  collectionToEdit: null,
  collectionToShare: null,
  exportTarget: null,
};

/**
 * createCollectionsSlice builds the collection-surface whole-slot setters.
 */
export const createCollectionsSlice: StateCreator<
  CollectionsSliceState & CollectionsSliceActions,
  [],
  [],
  CollectionsSliceActions
> = (set) => ({
  setActiveCollectionId: (activeCollectionId) => set({ activeCollectionId }),
  setCollectionsTarget: (collectionsTarget) => set({ collectionsTarget }),
  setCollectionToEdit: (collectionToEdit) => set({ collectionToEdit }),
  setCollectionToShare: (collectionToShare) => set({ collectionToShare }),
  setExportTarget: (exportTarget) => set({ exportTarget }),
});
